import React, { useEffect, useState } from "react";
import { IFilterReport } from "../../../../types/filter.type";
import moment from "moment";
import { ConcernType } from "../../../../types/report.type";
import { cloneDeep } from "lodash";
import { numeralCustom } from "../../../../utils/helpers";
import WrapperFilter from "../WrapperFilter";
import { Stack } from "@mui/material";
import FilterRadios from "../FilterRadios";
import FilterTimeFromTo2 from "../FilterTimeFromTo2";

const concerns: ConcernType[] = [
  {
    label: "Thời gian",
    value: "thoigian",
  },
];

type TimeConcernType = {
  thoi_gian: string;
  tien_hang: number;
  tien_ck: number;
  doanh_thu: number;
};

type TimeType =
  | "fromTo"
  | "today"
  | "yesterday"
  | "thisweek"
  | "lastweek"
  | "7daysago"
  | "thismonth"
  | "lastmonth"
  | "30daysago"
  | "thisquarter"
  | "lastquarter"
  | "thisyear"
  | "lastyear";

const timeConcerns: Record<TimeType, ConcernType> = {
  fromTo: {
    label: "From to",
    value: "fromTo",
    title: "Doanh thu quán ăn theo khoảng thời gian",
    api: "ctbanle",
    convertData: (data, queryObject) => {
      const result: TimeConcernType[] = [];
      const total = data.pop();
      result.push({
        thoi_gian: `${moment(queryObject.tu_ngay).format(
          "DD/MM/YYYY"
        )} - ${moment(queryObject.den_ngay).format("DD/MM/YYYY")}`,
        tien_hang: total?.tien_hang || 0,
        tien_ck: total?.tien_ck || 0,
        doanh_thu: total?.tien || 0,
      });
      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "300px",
        wrap: true,
        selector: (row) => row.thoi_gian,
      },
      {
        name: "Tiền hàng",
        width: "130px",
        right: true,
        selector: (row) => row.tien_hang,
        format: (row) => numeralCustom(row.tien_hang).format(),
      },
      {
        name: "Tiền CK",
        width: "130px",
        right: true,
        selector: (row) => row.tien_ck,
        format: (row) => numeralCustom(row.tien_ck).format(),
      },
      {
        name: "Doanh thu",
        width: "130px",
        right: true,
        selector: (row) => row.doanh_thu,
        format: (row) => numeralCustom(row.doanh_thu).format(),
      },
    ],
  },
  today: {
    label: "Today",
    value: "today",
    title: "Doanh thu quán ăn hôm nay",
    api: "ctbanle",
    convertData: (data) => {
      const result: TimeConcernType[] = [];
      const total = data.pop();
      result.push({
        thoi_gian: "Hôm nay",
        tien_hang: total?.tien_hang || 0,
        tien_ck: total?.tien_ck || 0,
        doanh_thu: total?.tien || 0,
      });
      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "300px",
        wrap: true,
        selector: (row) => row.thoi_gian,
      },
      {
        name: "Tiền hàng",
        width: "130px",
        right: true,
        selector: (row) => row.tien_hang,
        format: (row) => numeralCustom(row.tien_hang).format(),
      },
      {
        name: "Tiền CK",
        width: "130px",
        right: true,
        selector: (row) => row.tien_ck,
        format: (row) => numeralCustom(row.tien_ck).format(),
      },
      {
        name: "Doanh thu",
        width: "130px",
        right: true,
        selector: (row) => row.doanh_thu,
        format: (row) => numeralCustom(row.doanh_thu).format(),
      },
    ],
  },
  yesterday: {
    label: "Yesterday",
    value: "yesterday",
    title: "Doanh thu quán ăn hôm qua",
    api: "ctbanle",
    convertData: (data) => {
      const result: TimeConcernType[] = [];
      const total = data.pop();
      result.push({
        thoi_gian: "Hôm qua",
        tien_hang: total?.tien_hang || 0,
        tien_ck: total?.tien_ck || 0,
        doanh_thu: total?.tien || 0,
      });
      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "300px",
        wrap: true,
        selector: (row) => row.thoi_gian,
      },
      {
        name: "Tiền hàng",
        width: "130px",
        right: true,
        selector: (row) => row.tien_hang,
        format: (row) => numeralCustom(row.tien_hang).format(),
      },
      {
        name: "Tiền CK",
        width: "130px",
        right: true,
        selector: (row) => row.tien_ck,
        format: (row) => numeralCustom(row.tien_ck).format(),
      },
      {
        name: "Doanh thu",
        width: "130px",
        right: true,
        selector: (row) => row.doanh_thu,
        format: (row) => numeralCustom(row.doanh_thu).format(),
      },
    ],
  },
  thisweek: {
    label: "Thisweek",
    value: "thisweek",
    title: "Doanh thu quán ăn tuần này",
    api: "ctbanle",
    showChart: true,
    chartLabel: "Doanh thu",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.ngay);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: TimeConcernType[] = [];
      const total = data.pop();
      const startOfWeek = moment().startOf("isoWeeks");
      const dates = Array.from({ length: 7 }, (_, i) =>
        startOfWeek.clone().add(i, "days").date()
      );
      for (let i = 0; i < dates.length; i++) {
        const date = dates[i];
        const rowData = data
          .filter((item) => item.ngay === date)
          .reduce(
            (acc: Omit<TimeConcernType, "thoi_gian">, item: any) => {
              return {
                tien_hang: acc.tien_hang + item.tien_hang,
                tien_ck: acc.tien_ck + item.tien_ck,
                doanh_thu: acc.doanh_thu + item.tien,
              };
            },
            {
              tien_hang: 0,
              tien_ck: 0,
              doanh_thu: 0,
            } as Omit<TimeConcernType, "thoi_gian">
          );
        result.push({ thoi_gian: `Ngày ${date}`, ...rowData });
      }
      result.unshift({
        thoi_gian: "Tổng cộng",
        tien_hang: total.tien_hang,
        tien_ck: total.tien_ck,
        doanh_thu: total.tien,
      });

      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "300px",
        wrap: true,
        selector: (row) => row.thoi_gian,
      },
      {
        name: "Tiền hàng",
        width: "130px",
        right: true,
        selector: (row) => row.tien_hang,
        format: (row) => numeralCustom(row.tien_hang).format(),
      },
      {
        name: "Tiền CK",
        width: "130px",
        right: true,
        selector: (row) => row.tien_ck,
        format: (row) => numeralCustom(row.tien_ck).format(),
      },
      {
        name: "Doanh thu",
        width: "130px",
        right: true,
        selector: (row) => row.doanh_thu,
        format: (row) => numeralCustom(row.doanh_thu).format(),
      },
    ],
  },
  lastweek: {
    label: "Lastweek",
    value: "Lastweek",
    title: "Doanh thu quán ăn tuần trước",
    api: "ctbanle",
    showChart: true,
    chartLabel: "Doanh thu",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.ngay);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: TimeConcernType[] = [];
      const total = data.pop();
      const startOfLastWeek = moment().subtract(7, "days").startOf("isoWeeks");
      const dates = Array.from({ length: 7 }, (_, i) =>
        startOfLastWeek.clone().add(i, "days").date()
      );
      for (let i = 0; i < dates.length; i++) {
        const date = dates[i];
        const rowData = data
          .filter((item) => item.ngay === date)
          .reduce(
            (acc: Omit<TimeConcernType, "thoi_gian">, item: any) => {
              return {
                tien_hang: acc.tien_hang + item.tien_hang,
                tien_ck: acc.tien_ck + item.tien_ck,
                doanh_thu: acc.doanh_thu + item.tien,
              };
            },
            {
              tien_hang: 0,
              tien_ck: 0,
              doanh_thu: 0,
            } as Omit<TimeConcernType, "thoi_gian">
          );
        result.push({ thoi_gian: `Ngày ${date}`, ...rowData });
      }
      result.unshift({
        thoi_gian: "Tổng cộng",
        tien_hang: total.tien_hang,
        tien_ck: total.tien_ck,
        doanh_thu: total.tien,
      });

      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "300px",
        wrap: true,
        selector: (row) => row.thoi_gian,
      },
      {
        name: "Tiền hàng",
        width: "130px",
        right: true,
        selector: (row) => row.tien_hang,
        format: (row) => numeralCustom(row.tien_hang).format(),
      },
      {
        name: "Tiền CK",
        width: "130px",
        right: true,
        selector: (row) => row.tien_ck,
        format: (row) => numeralCustom(row.tien_ck).format(),
      },
      {
        name: "Doanh thu",
        width: "130px",
        right: true,
        selector: (row) => row.doanh_thu,
        format: (row) => numeralCustom(row.doanh_thu).format(),
      },
    ],
  },
  "7daysago": {
    label: "7daysago",
    value: "7daysago",
    title: "Doanh thu quán ăn 7 ngày qua",
    api: "ctbanle",
    showChart: true,
    chartLabel: "Doanh thu",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.ngay);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: TimeConcernType[] = [];
      const total = data.pop();
      const startOfLastWeek = moment().subtract(7, "days");
      const dates = Array.from({ length: 7 }, (_, i) =>
        startOfLastWeek.clone().add(i, "days").date()
      );
      for (let i = 0; i < dates.length; i++) {
        const date = dates[i];
        const rowData = data
          .filter((item) => item.ngay === date)
          .reduce(
            (acc: Omit<TimeConcernType, "thoi_gian">, item: any) => {
              return {
                tien_hang: acc.tien_hang + item.tien_hang,
                tien_ck: acc.tien_ck + item.tien_ck,
                doanh_thu: acc.doanh_thu + item.tien,
              };
            },
            {
              tien_hang: 0,
              tien_ck: 0,
              doanh_thu: 0,
            } as Omit<TimeConcernType, "thoi_gian">
          );
        result.push({ thoi_gian: `Ngày ${date}`, ...rowData });
      }
      result.unshift({
        thoi_gian: "Tổng cộng",
        tien_hang: total.tien_hang,
        tien_ck: total.tien_ck,
        doanh_thu: total.tien,
      });

      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "300px",
        wrap: true,
        selector: (row) => row.thoi_gian,
      },
      {
        name: "Tiền hàng",
        width: "130px",
        right: true,
        selector: (row) => row.tien_hang,
        format: (row) => numeralCustom(row.tien_hang).format(),
      },
      {
        name: "Tiền CK",
        width: "130px",
        right: true,
        selector: (row) => row.tien_ck,
        format: (row) => numeralCustom(row.tien_ck).format(),
      },
      {
        name: "Doanh thu",
        width: "130px",
        right: true,
        selector: (row) => row.doanh_thu,
        format: (row) => numeralCustom(row.doanh_thu).format(),
      },
    ],
  },
  thismonth: {
    label: "Thismonth",
    value: "Thismonth",
    title: "Doanh thu quán ăn tháng này",
    api: "ctbanle",
    showChart: true,
    chartLabel: "Doanh thu",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.ngay);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: TimeConcernType[] = [];
      const total = data.pop();
      const startOfMonth = moment().startOf("months");
      const daysInMonth = startOfMonth.clone().daysInMonth();
      const dates = Array.from({ length: daysInMonth }, (_, i) =>
        startOfMonth.clone().add(i, "days").date()
      );
      for (let i = 0; i < dates.length; i++) {
        const date = dates[i];
        const rowData = data
          .filter((item) => item.ngay === date)
          .reduce(
            (acc: Omit<TimeConcernType, "thoi_gian">, item: any) => {
              return {
                tien_hang: acc.tien_hang + item.tien_hang,
                tien_ck: acc.tien_ck + item.tien_ck,
                doanh_thu: acc.doanh_thu + item.tien,
              };
            },
            {
              tien_hang: 0,
              tien_ck: 0,
              doanh_thu: 0,
            } as Omit<TimeConcernType, "thoi_gian">
          );
        result.push({ thoi_gian: `Ngày ${date}`, ...rowData });
      }
      result.unshift({
        thoi_gian: "Tổng cộng",
        tien_hang: total.tien_hang,
        tien_ck: total.tien_ck,
        doanh_thu: total.tien,
      });

      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "300px",
        wrap: true,
        selector: (row) => row.thoi_gian,
      },
      {
        name: "Tiền hàng",
        width: "130px",
        right: true,
        selector: (row) => row.tien_hang,
        format: (row) => numeralCustom(row.tien_hang).format(),
      },
      {
        name: "Tiền CK",
        width: "130px",
        right: true,
        selector: (row) => row.tien_ck,
        format: (row) => numeralCustom(row.tien_ck).format(),
      },
      {
        name: "Doanh thu",
        width: "130px",
        right: true,
        selector: (row) => row.doanh_thu,
        format: (row) => numeralCustom(row.doanh_thu).format(),
      },
    ],
  },
  lastmonth: {
    label: "Lastmonth",
    value: "Lastmonth",
    title: "Doanh thu quán ăn tháng trước",
    api: "ctbanle",
    showChart: true,
    chartLabel: "Doanh thu",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.ngay);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: TimeConcernType[] = [];
      const total = data.pop();
      const startOfLastMonth = moment().subtract(1, "months").startOf("months");
      const daysInMonth = startOfLastMonth.clone().daysInMonth();
      const dates = Array.from({ length: daysInMonth }, (_, i) =>
        startOfLastMonth.clone().add(i, "days").date()
      );
      for (let i = 0; i < dates.length; i++) {
        const date = dates[i];
        const rowData = data
          .filter((item) => item.ngay === date)
          .reduce(
            (acc: Omit<TimeConcernType, "thoi_gian">, item: any) => {
              return {
                tien_hang: acc.tien_hang + item.tien_hang,
                tien_ck: acc.tien_ck + item.tien_ck,
                doanh_thu: acc.doanh_thu + item.tien,
              };
            },
            {
              tien_hang: 0,
              tien_ck: 0,
              doanh_thu: 0,
            } as Omit<TimeConcernType, "thoi_gian">
          );
        result.push({ thoi_gian: `Ngày ${date}`, ...rowData });
      }
      result.unshift({
        thoi_gian: "Tổng cộng",
        tien_hang: total.tien_hang,
        tien_ck: total.tien_ck,
        doanh_thu: total.tien,
      });

      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "300px",
        wrap: true,
        selector: (row) => row.thoi_gian,
      },
      {
        name: "Tiền hàng",
        width: "130px",
        right: true,
        selector: (row) => row.tien_hang,
        format: (row) => numeralCustom(row.tien_hang).format(),
      },
      {
        name: "Tiền CK",
        width: "130px",
        right: true,
        selector: (row) => row.tien_ck,
        format: (row) => numeralCustom(row.tien_ck).format(),
      },
      {
        name: "Doanh thu",
        width: "130px",
        right: true,
        selector: (row) => row.doanh_thu,
        format: (row) => numeralCustom(row.doanh_thu).format(),
      },
    ],
  },
  "30daysago": {
    label: "30daysago",
    value: "30daysago",
    title: "Doanh thu quán ăn 30 ngày qua",
    api: "ctbanle",
    showChart: true,
    chartLabel: "Doanh thu",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.ngay);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: TimeConcernType[] = [];
      const total = data.pop();
      const startOf30DaysAgo = moment().subtract(30, "days");
      const dates = Array.from({ length: 30 }, (_, i) =>
        startOf30DaysAgo.clone().add(i, "days").date()
      );
      for (let i = 0; i < dates.length; i++) {
        const date = dates[i];
        const rowData = data
          .filter((item) => item.ngay === date)
          .reduce(
            (acc: Omit<TimeConcernType, "thoi_gian">, item: any) => {
              return {
                tien_hang: acc.tien_hang + item.tien_hang,
                tien_ck: acc.tien_ck + item.tien_ck,
                doanh_thu: acc.doanh_thu + item.tien,
              };
            },
            {
              tien_hang: 0,
              tien_ck: 0,
              doanh_thu: 0,
            } as Omit<TimeConcernType, "thoi_gian">
          );
        result.push({ thoi_gian: `Ngày ${date}`, ...rowData });
      }
      result.unshift({
        thoi_gian: "Tổng cộng",
        tien_hang: total.tien_hang,
        tien_ck: total.tien_ck,
        doanh_thu: total.tien,
      });

      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "300px",
        wrap: true,
        selector: (row) => row.thoi_gian,
      },
      {
        name: "Tiền hàng",
        width: "130px",
        right: true,
        selector: (row) => row.tien_hang,
        format: (row) => numeralCustom(row.tien_hang).format(),
      },
      {
        name: "Tiền CK",
        width: "130px",
        right: true,
        selector: (row) => row.tien_ck,
        format: (row) => numeralCustom(row.tien_ck).format(),
      },
      {
        name: "Doanh thu",
        width: "130px",
        right: true,
        selector: (row) => row.doanh_thu,
        format: (row) => numeralCustom(row.doanh_thu).format(),
      },
    ],
  },
  thisquarter: {
    value: "thisquarter",
    label: "thisquarter",
    title: "Doanh thu quán ăn quý này",
    api: "ctbanle",
    showChart: true,
    chartLabel: "Doanh thu",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.thoigian);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: TimeConcernType[] = [];
      const total = data.pop();

      const startOfQuarter = moment().startOf("quarters");
      const monthsInQuarter = Array.from({ length: 3 }, (_, i) =>
        startOfQuarter.clone().add(i, "months").format("YYYY-MM-DD")
      );

      for (let i = 0; i < monthsInQuarter.length; i++) {
        const month = moment(monthsInQuarter[i]).format("M/YYYY");
        const items = data.filter(
          (item) => `${item.thang}/${item.nam}` === month
        );
        const rowData: Omit<TimeConcernType, "thoi_gian"> = items.reduce(
          (acc, item) => {
            return {
              tien_hang: acc.tien_hang + item.tien_hang,
              tien_ck: acc.tien_ck + item.tien_ck,
              doanh_thu: acc.doanh_thu + item.tien,
            };
          },
          { tien_hang: 0, tien_ck: 0, doanh_thu: 0 } as Omit<
            TimeConcernType,
            "thoi_gian"
          >
        );
        result.push({
          thoi_gian: `Tháng ${month}`,
          ...rowData,
        });
      }
      result.unshift({
        thoi_gian: "Tổng cộng",
        tien_hang: total.tien_hang,
        tien_ck: total.tien_ck,
        doanh_thu: total.tien,
      });
      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "300px",
        wrap: true,
        selector: (row) => row.thoi_gian,
      },
      {
        name: "Tiền hàng",
        width: "130px",
        right: true,
        selector: (row) => row.tien_hang,
        format: (row) => numeralCustom(row.tien_hang).format(),
      },
      {
        name: "Tiền CK",
        width: "130px",
        right: true,
        selector: (row) => row.tien_ck,
        format: (row) => numeralCustom(row.tien_ck).format(),
      },
      {
        name: "Doanh thu",
        width: "130px",
        right: true,
        selector: (row) => row.doanh_thu,
        format: (row) => numeralCustom(row.doanh_thu).format(),
      },
    ],
  },
  lastquarter: {
    value: "lastquarter",
    label: "lastquarter",
    title: "Doanh thu quán ăn quý trước",
    api: "ctbanle",
    showChart: true,
    chartLabel: "Doanh thu",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.thoigian);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: TimeConcernType[] = [];
      const total = data.pop();

      const startOfLastQuarter = moment()
        .subtract(1, "quarters")
        .startOf("quarters");
      const monthsInQuarter = Array.from({ length: 3 }, (_, i) =>
        startOfLastQuarter.clone().add(i, "months").format("YYYY-MM-DD")
      );

      for (let i = 0; i < monthsInQuarter.length; i++) {
        const month = moment(monthsInQuarter[i]).format("M/YYYY");
        const items = data.filter(
          (item) => `${item.thang}/${item.nam}` === month
        );
        const rowData: Omit<TimeConcernType, "thoi_gian"> = items.reduce(
          (acc, item) => {
            return {
              tien_hang: acc.tien_hang + item.tien_hang,
              tien_ck: acc.tien_ck + item.tien_ck,
              doanh_thu: acc.doanh_thu + item.tien,
            };
          },
          { tien_hang: 0, tien_ck: 0, doanh_thu: 0 } as Omit<
            TimeConcernType,
            "thoi_gian"
          >
        );
        result.push({
          thoi_gian: `Tháng ${month}`,
          ...rowData,
        });
      }
      result.unshift({
        thoi_gian: "Tổng cộng",
        tien_hang: total.tien_hang,
        tien_ck: total.tien_ck,
        doanh_thu: total.tien,
      });
      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "300px",
        wrap: true,
        selector: (row) => row.thoi_gian,
      },
      {
        name: "Tiền hàng",
        width: "130px",
        right: true,
        selector: (row) => row.tien_hang,
        format: (row) => numeralCustom(row.tien_hang).format(),
      },
      {
        name: "Tiền CK",
        width: "130px",
        right: true,
        selector: (row) => row.tien_ck,
        format: (row) => numeralCustom(row.tien_ck).format(),
      },
      {
        name: "Doanh thu",
        width: "130px",
        right: true,
        selector: (row) => row.doanh_thu,
        format: (row) => numeralCustom(row.doanh_thu).format(),
      },
    ],
  },
  thisyear: {
    value: "thisyear",
    label: "thisyear",
    title: "Doanh thu quán ăn năm nay",
    api: "ctbanle",
    showChart: true,
    chartLabel: "Doanh thu",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.thoigian);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: TimeConcernType[] = [];
      const total = data.pop();

      const startOfYear = moment().startOf("years");
      const monthsInYear = Array.from({ length: 12 }, (_, i) =>
        startOfYear.clone().add(i, "months").format("YYYY-MM-DD")
      );

      for (let i = 0; i < monthsInYear.length; i++) {
        const month = moment(monthsInYear[i]).format("M/YYYY");
        const items = data.filter(
          (item) => `${item.thang}/${item.nam}` === month
        );
        const rowData: Omit<TimeConcernType, "thoi_gian"> = items.reduce(
          (acc, item) => {
            return {
              tien_hang: acc.tien_hang + item.tien_hang,
              tien_ck: acc.tien_ck + item.tien_ck,
              doanh_thu: acc.doanh_thu + item.tien,
            };
          },
          { tien_hang: 0, tien_ck: 0, doanh_thu: 0 } as Omit<
            TimeConcernType,
            "thoi_gian"
          >
        );
        result.push({
          thoi_gian: `Tháng ${month}`,
          ...rowData,
        });
      }
      result.unshift({
        thoi_gian: "Tổng cộng",
        tien_hang: total.tien_hang,
        tien_ck: total.tien_ck,
        doanh_thu: total.tien,
      });
      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "300px",
        wrap: true,
        selector: (row) => row.thoi_gian,
      },
      {
        name: "Tiền hàng",
        width: "130px",
        right: true,
        selector: (row) => row.tien_hang,
        format: (row) => numeralCustom(row.tien_hang).format(),
      },
      {
        name: "Tiền CK",
        width: "130px",
        right: true,
        selector: (row) => row.tien_ck,
        format: (row) => numeralCustom(row.tien_ck).format(),
      },
      {
        name: "Doanh thu",
        width: "130px",
        right: true,
        selector: (row) => row.doanh_thu,
        format: (row) => numeralCustom(row.doanh_thu).format(),
      },
    ],
  },
  lastyear: {
    value: "thisyear",
    label: "thisyear",
    title: "Doanh thu quán ăn năm trước",
    api: "ctbanle",
    showChart: true,
    chartLabel: "Doanh thu",
    generateChartLabels: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.thoigian);
    },
    generateChartValues: (data) => {
      const clonedData = cloneDeep(data);
      clonedData.shift();
      return clonedData.map((item) => item.sl);
    },
    convertData: (data) => {
      const result: TimeConcernType[] = [];
      const total = data.pop();

      const startOfLastYear = moment().subtract(1, "years").startOf("years");
      const monthsInYear = Array.from({ length: 12 }, (_, i) =>
        startOfLastYear.clone().add(i, "months").format("YYYY-MM-DD")
      );

      for (let i = 0; i < monthsInYear.length; i++) {
        const month = moment(monthsInYear[i]).format("M/YYYY");
        const items = data.filter(
          (item) => `${item.thang}/${item.nam}` === month
        );
        const rowData: Omit<TimeConcernType, "thoi_gian"> = items.reduce(
          (acc, item) => {
            return {
              tien_hang: acc.tien_hang + item.tien_hang,
              tien_ck: acc.tien_ck + item.tien_ck,
              doanh_thu: acc.doanh_thu + item.tien,
            };
          },
          { tien_hang: 0, tien_ck: 0, doanh_thu: 0 } as Omit<
            TimeConcernType,
            "thoi_gian"
          >
        );
        result.push({
          thoi_gian: `Tháng ${month}`,
          ...rowData,
        });
      }
      result.unshift({
        thoi_gian: "Tổng cộng",
        tien_hang: total.tien_hang,
        tien_ck: total.tien_ck,
        doanh_thu: total.tien,
      });
      return result;
    },
    columns: [
      {
        name: "Thời gian",
        width: "300px",
        wrap: true,
        selector: (row) => row.thoi_gian,
      },
      {
        name: "Tiền hàng",
        width: "130px",
        right: true,
        selector: (row) => row.tien_hang,
        format: (row) => numeralCustom(row.tien_hang).format(),
      },
      {
        name: "Tiền CK",
        width: "130px",
        right: true,
        selector: (row) => row.tien_ck,
        format: (row) => numeralCustom(row.tien_ck).format(),
      },
      {
        name: "Doanh thu",
        width: "130px",
        right: true,
        selector: (row) => row.doanh_thu,
        format: (row) => numeralCustom(row.doanh_thu).format(),
      },
    ],
  },
};

function FilterRPTRestaurant({ setConcern, setQueryObject }: IFilterReport) {
  const originFilter = {
    timeFrom: moment().startOf("months").format("YYYY-MM-DD"),
    timeTo: moment().endOf("days").format("YYYY-MM-DD"),
  };
  const [filter, setFilter] = useState<{ [key: string]: any }>(originFilter);
  const [valueConcern, setValueConcern] = useState(concerns[0].value);
  const [timeOption, setTimeOption] = useState<any>();

  const handleRefreshFilter = () => {
    setFilter(originFilter);
  };

  useEffect(() => {
    const queryObject: { [key: string]: any } = { ma_ct: "pbl_nh" };
    if (filter.timeFrom) {
      queryObject.tu_ngay = moment(filter.timeFrom)
        .startOf("days")
        .toISOString();
    }
    if (filter.timeTo) {
      queryObject.den_ngay = moment(filter.timeTo).endOf("days").toISOString();
    }
    setQueryObject(queryObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    if (valueConcern !== "thoigian") {
      setConcern(concerns.find((item) => item.value === valueConcern));
    } else {
      let currentTimeConcern;
      if (timeOption?.value) {
        currentTimeConcern = timeConcerns[timeOption.value as TimeType];
      } else {
        currentTimeConcern = timeConcerns.fromTo;
      }
      setConcern(currentTimeConcern);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueConcern, timeOption?.value]);

  return (
    <WrapperFilter refreshFilter={handleRefreshFilter}>
      <Stack sx={{ width: "100%" }} gap={1}>
        <FilterRadios
          title="Thống kê theo"
          values={concerns}
          defaultValue={valueConcern}
          onChange={(newConcern) => setValueConcern(newConcern)}
        />
        <FilterTimeFromTo2
          showOptions
          defaultTimeFrom={filter.timeFrom}
          defaultTimeTo={filter.timeTo}
          title="Thời gian"
          onOptionChange={setTimeOption}
          onSearch={(time) => setFilter({ ...filter, ...time })}
        />
      </Stack>
    </WrapperFilter>
  );
}

export default FilterRPTRestaurant;
